import { memo, useMemo, type ComponentProps, type CSSProperties, type ReactNode } from "react";
import { Spin } from "antd";
import { TableVirtuoso } from "react-virtuoso";
import { cn } from "../../utils/cn";
import CustomEmpty from "../CustomSharedEmpty/CustomSharedEmpty";
import CustomTooltip from "../CustomSharedTooltip/CustomSharedTooltip";

export interface GroupedTableColumn<Group, Row> {
  key: string;
  title: ReactNode;
  width: number;
  renderGroup: (group: Group) => ReactNode;
  renderRow: (row: Row, group: Group) => ReactNode;
  className?: string;
  detailClassName?: string;
  ellipsis?: boolean;
}

export interface CustomGroupedTableProps<Group, Row> {
  groups: Group[];
  columns: GroupedTableColumn<Group, Row>[];
  getRows: (group: Group) => Row[];
  groupKey: (group: Group) => string | number;
  rowKey: (row: Row, group: Group) => string | number;
  height?: CSSProperties["height"];
  loading?: boolean;
  emptyText?: ReactNode;
  label: string;
  fitToWidth?: boolean;
}

type TableContext = { columns: { key: string; width: number }[]; label: string; fitToWidth: boolean };

const tableComponents = {
  Table: ({ context, style, children, ...props }: ComponentProps<"table"> & { context?: TableContext }) => (
    <table
      {...props}
      aria-label={context?.label}
      className="w-full table-fixed border-separate border-spacing-0 text-sm"
      style={{ ...style, minWidth: context?.fitToWidth ? undefined : context?.columns.reduce((total, column) => total + column.width, 0) }}
    >
      <colgroup>
        {context?.columns.map((column) => (
          <col key={column.key} style={{ width: context.fitToWidth
            ? `${column.width / context.columns.reduce((total, current) => total + current.width, 0) * 100}%`
            : column.width }} />
        ))}
      </colgroup>
      {children}
    </table>
  ),
};

function CustomGroupedTable<Group, Row>({
  groups,
  columns,
  getRows,
  groupKey,
  rowKey,
  height = "min(650px, 65vh)",
  loading = false,
  emptyText,
  label,
  fitToWidth = false,
}: CustomGroupedTableProps<Group, Row>) {
  const items = useMemo(() => groups.flatMap((group) => [
    { group, row: null as Row | null, key: `group:${groupKey(group)}` },
    ...getRows(group).map((row) => ({ group, row, key: `group:${groupKey(group)}:row:${rowKey(row, group)}` })),
  ]), [groups, getRows, groupKey, rowKey]);
  const context = useMemo(() => ({ columns, label, fitToWidth }), [columns, label, fitToWidth]);

  return (
    <Spin spinning={loading}>
      {items.length ? (
        <TableVirtuoso
          data={items}
          context={context}
          components={tableComponents}
          style={{ height }}
          computeItemKey={(_index, item) => item.key}
          increaseViewportBy={200}
          fixedHeaderContent={() => (
            <tr>
              {columns.map((column) => (
                <th key={column.key} scope="col" className={cn(
                  "h-[52px] bg-primary6 py-2 text-left font-semibold text-black whitespace-normal break-words",
                  fitToWidth ? "px-2 first:pl-4" : "px-4 first:pl-8",
                )}>
                  {column.title}
                </th>
              ))}
            </tr>
          )}
          itemContent={(_index, item) => columns.map((column, columnIndex) => {
            const value = item.row === null ? column.renderGroup(item.group) : column.renderRow(item.row, item.group);
            return (
            <td
              key={column.key}
              className={cn(
                "border-b border-primary6 align-middle break-words",
                fitToWidth ? "overflow-hidden px-2 first:pl-4" : "px-4 first:pl-8",
                item.row === null
                  ? "h-[52px] bg-primary-shade font-bold text-white"
                  : "h-[38px] bg-white text-gray12",
                item.row !== null && columnIndex === 0 && "bg-primary6",
                column.className,
                item.row !== null && column.detailClassName,
              )}
            >
              {column.ellipsis && (typeof value === "string" || typeof value === "number") ? (
                <CustomTooltip title={value} placement="top">
                  <span className="inline-block max-w-full truncate align-middle">{value}</span>
                </CustomTooltip>
              ) : value}
            </td>
            );
          })}
        />
      ) : (
        <div className="flex min-h-[300px] items-center justify-center">
          <CustomEmpty description={emptyText} />
        </div>
      )}
    </Spin>
  );
}

export default memo(CustomGroupedTable) as typeof CustomGroupedTable;
